import React, { useRef, useState } from 'react';
import { cn } from '../../utils/tw';

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  maxFiles?: number;
  acceptedTypes?: string[];
  maxSizePerFile?: number; // in MB
}

export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  disabled = false,
  error,
  required = false,
  maxFiles = 5,
  acceptedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'],
  maxSizePerFile = 10 // 10MB default
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles = Array.from(selectedFiles);
    const validFiles: File[] = [];
    
    // Validate files
    for (const file of newFiles) {
      // Check file size
      if (file.size > maxSizePerFile * 1024 * 1024) {
        continue; // Skip files that are too large
      }
      
      // Check file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedTypes.includes(fileExtension)) {
        continue; // Skip invalid file types
      }
      
      validFiles.push(file);
    }

    // Combine with existing files, respecting maxFiles limit
    const combinedFiles = [...files, ...validFiles].slice(0, maxFiles);
    onFilesChange(combinedFiles);
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
          dragActive && 'border-primary bg-primary/5',
          !dragActive && 'border-gray-300 hover:border-gray-400',
          error && 'border-error',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => !disabled && fileInputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
          aria-label="Upload files"
        />
        
        <div className="space-y-2">
          <div className="text-2xl">📁</div>
          <div className="text-sm">
            <span className="font-medium text-primary">Click to upload</span> or drag and drop
          </div>
          <div className="text-xs text-mutedText">
            {acceptedTypes.join(', ')} up to {maxSizePerFile}MB each
          </div>
          {required && (
            <div className="text-xs text-error">
              At least one file is required *
            </div>
          )}
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            Uploaded Files ({files.length}/{maxFiles})
          </div>
          <div className="space-y-1">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded border"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={disabled}
                  className={cn(
                    'ml-2 p-1 text-error hover:bg-error/10 rounded transition-colors',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-error text-xs animate-fadeIn">{error}</p>
      )}

      {/* Helper Text */}
      <div className="text-xs text-mutedText">
        Upload receipts, invoices, or other supporting documents
      </div>
    </div>
  );
};

export default FileUpload;